import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface IOSCalendarUrlDialogProps {
  open: boolean;
  initialUrl?: string;
  onClose: () => void;
  onSave: (url: string) => void;
  loading?: boolean;
  error?: string;
  success?: string;
}

const IOSCalendarUrlDialog = ({ open, initialUrl, onClose, onSave, loading, error, success }: IOSCalendarUrlDialogProps) => {
  const { t, i18n } = useTranslation();
  const [url, setUrl] = useState(initialUrl || '');

  // Reset URL when dialog opens
  React.useEffect(() => {
    setUrl(initialUrl || '');
  }, [initialUrl, open]);

  const currentLanguage = i18n.language;
  let tutorialLink = 'https://cms.hkstylist.com/how-to-get-your-ios-calendar-public-url-tutorial/';
  if (currentLanguage === 'zh' || currentLanguage === 'zh-HK') {
    tutorialLink = 'https://cms.hkstylist.com/%e5%a6%82%e4%bd%95%e7%8d%b2%e5%8f%96%e6%82%a8%e7%9a%84-ios-%e6%97%a5%e6%9b%86%e5%85%ac%e9%96%8burl%ef%bc%88%e6%95%99%e5%ad%b8%ef%bc%89/';
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('reservationsPage.iosCalendarDialogTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" mb={2}>
          {t('reservationsPage.iosCalendarDialogDescription')}
        </Typography>
        <TextField
          label={t('reservationsPage.iosCalendarUrlLabel')}
          value={url}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
          fullWidth
          margin="normal"
          autoFocus
        />
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          <a
            href={tutorialLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1976d2', textDecoration: 'underline' }}
          >
            {t('reservationsPage.iosCalendarTutorialLink')}
          </a>
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t('reservationsPage.iosCalendarDialogCancel')}
        </Button>
        <Button
          onClick={() => onSave(url)}
          variant="contained"
          disabled={loading}
        >
          {t('reservationsPage.iosCalendarDialogConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IOSCalendarUrlDialog;
